import { Pagination, useDisclosure } from '@heroui/react';
import { useMemo, useState, useEffect } from 'react';
import { useFetchProviderPortfolioDetails } from '../../../hook/useQueryData';
import SmallLoadingSpinner from '../../components/common/ui/SmallLoadingSpinner';
import NotFound from '../../../utils/erros/NotFound';
import {
  FaStar,
  FaRegStar,
  FaImage,
  FaChevronLeft,
  FaChevronRight,
  FaLocationDot,
} from 'react-icons/fa6';
import { resolveStorageUrl } from '../Customer/aws/s3FileUpload';
import ViewJobDetails from './ViewJobDetails';

const REVIEW_IMAGES_FOLDER = 'review-images';
const DEFAULT_IMAGE_URL =
  'https://cdn.hirepanther.ca/common/default-fallback-image.png';

const ImageSkeleton = () => {
  return (
    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer rounded-lg" />
  );
};

const ImageLoadingOverlay = ({ isLoading }: { isLoading: boolean }) => {
  if (!isLoading) return null;

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-xs z-20">
      <div className="flex flex-col items-center gap-2">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
        <span className="text-xs text-gray-600 font-medium">Loading</span>
      </div>
    </div>
  );
};

const PortFolio = ({ id }: any) => {
  const descriptionModal = useDisclosure();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<{
    [key: string]: number;
  }>({});
  const [imageCache, setImageCache] = useState<{ [key: string]: string[] }>({});
  const [imageLoadingState, setImageLoadingState] = useState<{
    [key: string]: boolean;
  }>({});

  const { data, isPending, isError, error, refetch } =
    useFetchProviderPortfolioDetails(id, currentPage, 8);

  const jobs = data?.items;
  const totalPage = useMemo(() => data?.pages || 1, [data]);

  // Reset image states when page changes
  useEffect(() => {
    setLoadedImages(new Set());
    setCurrentImageIndex({});
    setImageLoadingState({});
  }, [currentPage]);

  // Cache images when data changes
  useEffect(() => {
    if (jobs && jobs.length > 0) {
      const newCache: { [key: string]: string[] } = {};
      const newLoadingState: { [key: string]: boolean } = {};

      jobs.forEach((job: any) => {
        const images = getJobImages(job);
        if (images.length > 0) {
          newCache[job?.jobCardId || ''] = images;
          images.forEach((img) => {
            newLoadingState[img] = true;
          });
        } else {
          // If no images, use default image
          newCache[job?.jobCardId || ''] = [DEFAULT_IMAGE_URL];
        }
      });

      setImageCache(newCache);
      setImageLoadingState(newLoadingState);
    }
  }, [jobs]);

  const getJobImages = (job: any): string[] => {
    if (!job) return [];

    const imageUrls: string[] = [];

    if (Array.isArray(job.selectedImages) && job.selectedImages.length > 0) {
      job.selectedImages.forEach((img: any) => {
        const rawUrl =
          typeof img === 'string'
            ? img
            : img?.imageUrl || img?.url || img?.fullUrl || img?.src || '';
        if (rawUrl) {
          const resolved = resolveStorageUrl(rawUrl, REVIEW_IMAGES_FOLDER);
          if (resolved && !imageUrls.includes(resolved)) {
            imageUrls.push(resolved);
          }
        }
      });
    } else if (Array.isArray(job.images) && job.images.length > 0) {
      job.images.forEach((img: any) => {
        const rawUrl =
          typeof img === 'string'
            ? img
            : img?.imageUrl || img?.url || img?.fullUrl || img?.src || '';
        if (rawUrl) {
          const resolved = resolveStorageUrl(rawUrl, REVIEW_IMAGES_FOLDER);
          if (resolved && !imageUrls.includes(resolved)) {
            imageUrls.push(resolved);
          }
        }
      });
    }

    // If no images found, return default image
    if (imageUrls.length === 0) {
      return [DEFAULT_IMAGE_URL];
    }

    return imageUrls;
  };

  const handleImageLoad = (url: string) => {
    setLoadedImages((prev) => new Set(prev).add(url));
    setImageLoadingState((prev) => ({
      ...prev,
      [url]: false,
    }));
  };

  const handleImageError = (
    url: string,
    e: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const target = e.currentTarget;

    // If already using default image, don't retry
    if (url === DEFAULT_IMAGE_URL) {
      setImageLoadingState((prev) => ({
        ...prev,
        [url]: false,
      }));
      return;
    }

    if (loadedImages.has(url)) return;

    const retryCount = parseInt(target.getAttribute('data-retry-count') || '0');

    if (retryCount >= 2) {
      // After retries, use default image
      target.src = DEFAULT_IMAGE_URL;
      setImageLoadingState((prev) => ({
        ...prev,
        [url]: false,
      }));
      return;
    }

    target.setAttribute('data-retry-count', String(retryCount + 1));

    const fileName = url.split('/').pop();
    if (fileName) {
      const fallbackResolved = resolveStorageUrl(
        fileName,
        REVIEW_IMAGES_FOLDER
      );
      if (fallbackResolved && fallbackResolved !== url) {
        target.src = fallbackResolved;
      }
    }
  };

  const getRatingValue = (job: any): number => {
    if (job?.ratingId) {
      const ratingNum = Number(job.ratingId);
      if (!isNaN(ratingNum) && ratingNum > 0) {
        return ratingNum;
      }
    }
    return job?.rating || 0;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleDescriptionClick = (job: any) => {
    setSelectedJob(job);
    descriptionModal.onOpen();
  };

  const goToPreviousImage = (jobCardId: string, totalImages: number) => {
    setCurrentImageIndex((prev) => ({
      ...prev,
      [jobCardId]:
        (prev[jobCardId] || 0) > 0
          ? (prev[jobCardId] || 0) - 1
          : totalImages - 1,
    }));
  };

  const goToNextImage = (jobCardId: string, totalImages: number) => {
    setCurrentImageIndex((prev) => ({
      ...prev,
      [jobCardId]: ((prev[jobCardId] || 0) + 1) % totalImages,
    }));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    refetch();
  };

  const getCachedOrComputedImages = (job: any) => {
    const jobId = job?.jobCardId || '';
    if (imageCache[jobId]) {
      return imageCache[jobId];
    }
    return getJobImages(job);
  };

  return (
    <div className="mt-5">
      {/* Add shimmer animation to global styles */}
      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: calc(200% + 20px) 0;
          }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-shimmer {
            animation: none;
            background: #f0f0f0;
          }
        }
      `}</style>

      {isPending ? (
        <div className="flex justify-center items-center mt-10">
          <SmallLoadingSpinner label="Loading portfolio..." />
        </div>
      ) : isError ? (
        <div className="flex justify-center items-center mt-10">
          <p className="text-danger text-center">
            {error instanceof Error
              ? error.message
              : error || 'Something went wrong'}
          </p>
        </div>
      ) : jobs?.length === 0 ? (
        <NotFound />
      ) : (
        <>
          {/* Grid Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {jobs.map((item: any, index: number) => {
              const jobImages = getCachedOrComputedImages(item);
              const currentIndex = currentImageIndex[item?.jobCardId] || 0;
              const currentImage =
                jobImages.length > 0
                  ? jobImages[currentIndex]
                  : DEFAULT_IMAGE_URL;
              const ratingValue = getRatingValue(item);
              const imageCount = jobImages.length;
              const hasMultipleImages = imageCount > 1;
              const isImageLoading = currentImage
                ? imageLoadingState[currentImage]
                : false;

              return (
                <div
                  key={item?.jobCardId || index}
                  className="group relative bg-white rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-primary-200 flex flex-col h-full"
                >
                  {/* Image Container with Carousel */}
                  <div className="relative w-full h-48 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden flex-shrink-0">
                    {currentImage ? (
                      <>
                        {/* Show skeleton while loading */}
                        {isImageLoading && (
                          <div className="absolute inset-0 z-10">
                            <ImageSkeleton />
                          </div>
                        )}

                        {/* Loading Overlay */}
                        <ImageLoadingOverlay isLoading={isImageLoading} />

                        <img
                          key={`${currentImage}-${currentPage}`}
                          src={currentImage}
                          alt={item?.serviceName || 'Portfolio image'}
                          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
                            isImageLoading ? 'opacity-0' : 'opacity-100'
                          }`}
                          loading="lazy"
                          data-retry-count="0"
                          onLoad={() => handleImageLoad(currentImage)}
                          onError={(e) => handleImageError(currentImage, e)}
                        />

                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                        {/* Previous Button */}
                        {hasMultipleImages && !isImageLoading && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              goToPreviousImage(item?.jobCardId, imageCount);
                            }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-1.5 transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 hover:scale-110 shadow-md"
                            aria-label="Previous image"
                          >
                            <FaChevronLeft size={12} />
                          </button>
                        )}

                        {/* Next Button */}
                        {hasMultipleImages && !isImageLoading && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              goToNextImage(item?.jobCardId, imageCount);
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-1.5 transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 hover:scale-110 shadow-md"
                            aria-label="Next image"
                          >
                            <FaChevronRight size={12} />
                          </button>
                        )}

                        {/* Image Counter and Indicator Dots */}
                        {hasMultipleImages && !isImageLoading && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/40 to-transparent p-3 flex items-center justify-between">
                            <span className="text-white text-xs font-medium px-2 py-0.5 bg-black/30 rounded-full backdrop-blur-sm">
                              {currentIndex + 1} / {imageCount}
                            </span>

                            <div className="flex gap-1">
                              {jobImages.map((_: string, idx: number) => (
                                <div
                                  key={idx}
                                  className={`h-1.5 rounded-full transition-all duration-300 ${
                                    idx === currentIndex
                                      ? 'bg-white w-4'
                                      : 'bg-white/40 w-1.5'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                        <div className="text-center">
                          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-2">
                            <FaImage className="text-gray-400 text-xl" />
                          </div>
                          <p className="text-xs text-gray-400">No images</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4 flex flex-col flex-1">
                    {/* Service Name */}
                    <h3 className="text-base font-semibold text-gray-800 line-clamp-1 mb-1 group-hover:text-primary-600 transition-colors">
                      {item?.serviceName || 'Untitled Project'}
                    </h3>

                    {/* Location  */}
                    {item?.location && (
                      <div className="flex items-center gap-1.5 mb-2 text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-md w-fit">
                        <FaLocationDot
                          size={11}
                          className="text-primary-500 flex-shrink-0"
                        />
                        <span
                          className="truncate max-w-[180px]"
                          title={item.location}
                        >
                          {item.location}
                        </span>
                      </div>
                    )}

                    {/* Rating Stars */}
                    {item?.showRating && ratingValue > 0 && (
                      <div className="flex items-center gap-1.5 mb-2">
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }, (_, i) =>
                            i < Math.round(ratingValue) ? (
                              <FaStar
                                key={i}
                                size={12}
                                className="text-yellow-400 fill-yellow-400"
                              />
                            ) : (
                              <FaRegStar
                                key={i}
                                size={12}
                                className="text-gray-300"
                              />
                            )
                          )}
                        </div>
                        <span className="text-xs font-medium text-gray-600">
                          {ratingValue.toFixed(1)}
                        </span>
                      </div>
                    )}

                    {/* Description */}
                    {item?.description && (
                      <p className="text-xs text-gray-500 line-clamp-2 mb-3 leading-relaxed flex-1">
                        {item.description}
                      </p>
                    )}

                    {/* Meta Info */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-auto">
                      {item?.createdAt && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-400">
                          <span>{formatDate(item.createdAt)}</span>
                        </div>
                      )}

                      <button
                        onClick={() => handleDescriptionClick(item)}
                        className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 transition-colors"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Job Details Modal */}
      <ViewJobDetails
        isOpen={descriptionModal.isOpen}
        onOpenChange={descriptionModal.onOpenChange}
        job={selectedJob}
      />

      {/* Pagination */}
      {totalPage > 1 && (
        <div className="flex justify-end items-end mt-8 pt-4 border-t border-gray-100">
          <Pagination
            className="pl-5"
            showControls
            size="sm"
            page={currentPage}
            total={totalPage}
            onChange={handlePageChange}
            color="primary"
            variant="flat"
          />
        </div>
      )}
    </div>
  );
};

export default PortFolio;
